import React, { useCallback, useRef, useState } from 'react';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DateTimePicker,
  InputBase,
  Modal,
  TextField,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { cleanState } from '@ublend-npm/aula-editor';
import { ManageExtensionStyles } from './ManageExtension.types';
import ExtensionDescriptionEditor, {
  ExtensionDescriptionEditorRef,
} from './ExtensionDescriptionEditor';

type DateField = 'startDate' | 'endDate' | 'gradesReleasedAt';

type Props = {
  description: string;
  endDate: string;
  gradesReleasedAt: string;
  isHidden: boolean;
  isHandin: boolean;
  isTurnitin: boolean;
  onDescriptionChange: (value: string) => void;
  onEditorStatusChange: (status: {
    isPersistable: boolean;
    isUpdating: boolean;
  }) => void;
  onEndDateChange: (value: string) => void;
  onGradesReleasedAtChange: (value: string) => void;
  onHiddenChange: (value: boolean) => void;
  onRestore: () => void;
  onStartDateChange: (value: string) => void;
  originalDescription: string;
  originalEndDate: string;
  originalGradesReleasedAt: string;
  originalIsHidden: boolean;
  originalStartDate: string;
  spaceId: string;
  startDate: string;
  styles: ManageExtensionStyles;
};

const ExtensionPropertiesStep = ({
  description,
  endDate,
  gradesReleasedAt,
  isHidden,
  isHandin,
  isTurnitin,
  onDescriptionChange,
  onEditorStatusChange,
  onEndDateChange,
  onGradesReleasedAtChange,
  onHiddenChange,
  onRestore,
  onStartDateChange,
  originalDescription,
  originalEndDate,
  originalGradesReleasedAt,
  originalIsHidden,
  originalStartDate,
  spaceId,
  startDate,
  styles,
}: Props) => {
  const [openDateField, setOpenDateField] = useState<DateField>();
  const [descriptionEditorVersion, setDescriptionEditorVersion] = useState(0);
  const descriptionEditorRef = useRef<ExtensionDescriptionEditorRef>(null);
  const propertiesStatusRef = useRef<HTMLDivElement>(null);
  const [pendingDate, setPendingDate] = useState<Date>();
  const dateValues = { startDate, endDate, gradesReleasedAt };
  const dateChangeHandlers = {
    startDate: onStartDateChange,
    endDate: onEndDateChange,
    gradesReleasedAt: onGradesReleasedAtChange,
  };
  const openDatePicker = (field: DateField) => {
    setPendingDate(
      dateValues[field] ? new Date(dateValues[field]) : new Date()
    );
    setOpenDateField(field);
  };
  const closeDatePicker = () => {
    setOpenDateField(undefined);
    setPendingDate(undefined);
  };
  const formatDate = (value: string) =>
    value ? moment(value).format('lll') : '';
  const getEditorState = useCallback(() => {
    if (!description) return cleanState;
    try {
      return JSON.parse(description);
    } catch (_error) {
      return cleanState;
    }
  }, [description]);
  const datesMatch = (current: string, original: string) => {
    if (!current && !original) return true;
    return new Date(current).getTime() === new Date(original).getTime();
  };
  const isDescriptionEdited = description !== originalDescription;
  const isStartDateEdited = !datesMatch(startDate, originalStartDate);
  const isEndDateEdited = !datesMatch(endDate, originalEndDate);
  const isGradesReleasedAtEdited = !datesMatch(
    gradesReleasedAt,
    originalGradesReleasedAt
  );
  const editedProperties = [
    isDescriptionEdited && 'Assignment description',
    isStartDateEdited && 'Open date',
    isEndDateEdited && 'Due date',
    isGradesReleasedAtEdited && 'Grades release date',
    isHidden !== originalIsHidden && 'Visibility settings',
  ].filter(Boolean);
  const dateFieldIsEdited = {
    startDate: isStartDateEdited,
    endDate: isEndDateEdited,
    gradesReleasedAt: isGradesReleasedAtEdited,
  };
  const { palette } = useTheme();
  const isDark = palette.mode == 'dark';
  const renderDateField = (field: DateField, label: string) => (
    <TextField
      label={label}
      value={formatDate(dateValues[field])}
      helperText={dateFieldIsEdited[field] ? 'Edited' : ''}
      sx={{
        ...styles.dateField,
        '& .MuiFormHelperText-root': {
          fontSize: '12px',
          marginTop: '4px',
          mx: '0px',
          mb: '0px',
          color: isDark ? '#FFFFFFB2' : '#00000099',
          display: dateFieldIsEdited[field] ? 'block' : 'none',
        },
      }}
      inputProps={{
        'aria-label': label,
        readOnly: true,
        style: { cursor: 'pointer' },
      }}
      onClick={() => openDatePicker(field)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openDatePicker(field);
        }
      }}
    />
  );

  return (
    <Box sx={styles.properties}>
      <Box>
        <Box
          component={isHandin ? 'div' : 'label'}
          role={isHandin ? 'group' : undefined}
          aria-labelledby={
            isHandin ? 'assignment-description-label' : undefined
          }
          sx={{
            ...styles.field,
            ...(!isHandin && styles.descriptionTextField),
          }}
          onClick={(event) => {
            const isInteractiveTarget =
              event.target instanceof Element &&
              event.target.closest(
                '[contenteditable="true"], button, a, input, textarea, [role="menuitem"]'
              );
            if (
              isHandin &&
              !isInteractiveTarget &&
              descriptionEditorRef.current
            ) {
              descriptionEditorRef.current.focus();
            }
          }}
        >
          <Typography
            id="assignment-description-label"
            component="span"
            sx={styles.fieldLabel}
          >
            Assignment description
          </Typography>
          {isHandin ? (
            <Box sx={styles.descriptionWrapper}>
              <Box sx={styles.descriptionEditor}>
                <ExtensionDescriptionEditor
                  key={descriptionEditorVersion}
                  ref={descriptionEditorRef}
                  editorState={getEditorState()}
                  onChange={(value) =>
                    onDescriptionChange(JSON.stringify(value))
                  }
                  onFilesStatusChange={({
                    hasError: editorHasError,
                    isPersistable: editorIsPersistable,
                    isUpdating: editorIsUpdating,
                  }) =>
                    onEditorStatusChange({
                      isUpdating: editorIsUpdating,
                      isPersistable: editorIsPersistable && !editorHasError,
                    })
                  }
                  spaceId={spaceId}
                />
              </Box>
            </Box>
          ) : (
            <InputBase
              multiline
              rows={5}
              value={description}
              onChange={(event) => onDescriptionChange(event.target.value)}
              fullWidth
            />
          )}
        </Box>
        {isDescriptionEdited && (
          <Typography sx={styles.editedHelperText}>
            {isDescriptionEdited ? 'Edited' : ' '}
          </Typography>
        )}
      </Box>
      <Box sx={styles.dateFields}>
        {renderDateField('startDate', 'Open date')}
        {renderDateField('endDate', 'Due date')}
        {isTurnitin &&
          renderDateField('gradesReleasedAt', 'Grades release date')}
      </Box>
      <Box sx={styles.switchRow}>
        <Typography>Hide this extension</Typography>
        <Checkbox
          kind="switch"
          checked={isHidden}
          sx={styles.checkboxStyle}
          onChange={() => onHiddenChange(!isHidden)}
          label=""
          aria-label="Hide this extension"
        />
      </Box>
      <Box ref={propertiesStatusRef} tabIndex={-1}>
        <Alert role="status" severity="info" sx={styles.propertiesInfo}>
          {editedProperties.length ? (
            <Box sx={styles.editedPropertiesAlert}>
              <Typography>
                Properties edited: {editedProperties.join(', ')}
              </Typography>
              <Button
                type="button"
                kind="secondary"
                onClick={() => {
                  if (propertiesStatusRef.current)
                    propertiesStatusRef.current.focus();
                  onRestore();
                  setDescriptionEditorVersion((version) => version + 1);
                }}
              >
                Restore original properties
              </Button>
            </Box>
          ) : (
            <>You haven&apos;t edited any assignment properties yet.</>
          )}
        </Alert>
      </Box>

      <Modal open={Boolean(openDateField)} onClose={closeDatePicker}>
        <Box sx={styles.datePickerContainer}>
          <Typography sx={styles.datePickerTitle}>
            Select date &amp; time
          </Typography>
          <DateTimePicker
            key={openDateField}
            value={pendingDate || new Date()}
            onChange={(date: any) => {
              const value =
                date instanceof Date ? date : date?.toDate?.() ?? null;
              if (value) setPendingDate(value);
            }}
            {...(openDateField === 'startDate' && endDate
              ? { maxDate: new Date(endDate) }
              : {})}
            {...(openDateField === 'endDate' && startDate
              ? { minDate: new Date(startDate) }
              : {})}
            {...(openDateField === 'gradesReleasedAt' && endDate
              ? { minDate: new Date(endDate) }
              : {})}
            sx={styles.dateTimePicker}
          />
          <Box sx={styles.datePickerActions}>
            <Button type="button" kind="secondary" onClick={closeDatePicker}>
              Cancel
            </Button>
            <Button
              type="button"
              kind="secondary"
              onClick={() => {
                if (openDateField && pendingDate) {
                  dateChangeHandlers[openDateField](
                    new Date(pendingDate).toISOString()
                  );
                }
                closeDatePicker();
              }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ExtensionPropertiesStep;
